import React, { useEffect, useState } from "react";
import { Popover } from "@mui/material";
import { Colors } from "../../../../Constants/colors";
import { FontFamily } from "../../../../Constants/Enums/FontFamily.enums";
import { BtnSize, BtnType, Button } from "../../../Button";

interface Props {
  value: string;
  studentName: string;
  onCommit: (value: string) => unknown;
}

const RemarkIcon = ({ filled }: { filled: boolean }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 4h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9l-5 4V5a1 1 0 0 1 1-1z"
      fill={filled ? Colors.TangerineOrange : "none"}
      fillOpacity={filled ? 0.15 : 0}
      stroke={filled ? Colors.TangerineOrange : Colors.Grey}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * A single, one-line-sentence remark doesn't fit comfortably inside a
 * narrow spreadsheet cell. Instead of forcing every row to reserve a wide
 * column, this renders a compact button showing a preview of the remark,
 * and typing happens in a roomy popover textarea anchored to that row.
 */
export const RemarksCell = ({ value, studentName, onCommit }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (draft !== (value || "")) {
      onCommit(draft);
    }
  };

  const hasValue = !!value?.trim();

  return (
    <>
      <button
        type="button"
        className={`rs-remarks-btn${hasValue ? " rs-remarks-btn-filled" : ""}`}
        onClick={handleOpen}
        title={hasValue ? value : "Add a remark"}
      >
        <RemarkIcon filled={hasValue} />
        <span className="rs-remarks-preview">
          {hasValue ? value : "Add remark"}
        </span>
      </button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div style={{ padding: "14px", width: "320px" }}>
          <div
            style={{
              fontFamily: FontFamily.ROBOTO_REGULAR,
              fontSize: "12px",
              fontWeight: 600,
              color: Colors.DarkGrey,
              marginBottom: "8px",
            }}
          >
            Remark for {studentName}
          </div>
          <textarea
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Write a short remark about the student..."
            rows={3}
            style={{
              width: "100%",
              resize: "vertical",
              boxSizing: "border-box",
              border: `1.5px solid ${Colors.LightGrey}`,
              borderRadius: "8px",
              padding: "10px 12px",
              fontSize: "13px",
              fontFamily: FontFamily.ROBOTO_REGULAR,
              color: Colors.Black,
              outline: "none",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Button
              text="Done"
              btnType={BtnType.primary}
              btnSize={BtnSize.sm}
              onClick={handleClose}
            />
          </div>
        </div>
      </Popover>
    </>
  );
};
